/* Medidas que o médico faz sobre a chapa, na tela de laudo.
 *
 * Uma medida é um compasso HORIZONTAL: dois pontos na mesma altura. É assim que
 * o diâmetro transverso é definido -- não é uma reta qualquer sobre a imagem --
 * e travar a horizontal tira do médico a chance de errar o ângulo sem perceber.
 * A altura de cada compasso é livre e independente: o coração é mais largo numa
 * altura, e o tórax se mede na altura do diafragma.
 *
 * As coordenadas são FRAÇÃO da imagem (0 a 1), nunca pixel. A mesma chapa existe
 * em três tamanhos (miniatura, web, laudo) e o PDF usa outro ainda; fração
 * atravessa todos. E como o índice é uma razão entre duas larguras da MESMA
 * imagem, o fator de escala se cancela -- o número é o mesmo em qualquer tamanho,
 * sem calibração. É o que faz a régua funcionar também nas chapas antigas, de
 * que não se conhece o tamanho do pixel.
 */

export interface Compasso {
  /** Altura em que a medida foi tomada. Fração de 0 a 1. */
  y: number;
  x1: number;
  x2: number;
}

export interface MedidaDaImagem {
  /** Maior diâmetro transverso do coração. */
  cardiaco: Compasso | null;
  /** Maior largura interna do tórax. */
  toracico: Compasso | null;
}

/** Chave: id da imagem. O exame tem várias chapas e a medida é de uma só. */
export type Medidas = Record<string, MedidaDaImagem>;

/** O que a chapa precisa saber para falar em centímetro. Vem do DICOM, por
 *  imagem, e falta nas chapas que chegaram antes da migração 030. */
export interface Calibracao {
  /** Milímetros por pixel do JPEG de laudo. */
  mmPorPixel: number | null;
  /** Largura em pixels do JPEG de laudo. */
  larguraPx: number | null;
}

/** Referência usual do índice cardiotorácico no adulto, em PA. Não é um
 *  diagnóstico e o sistema não julga por ela: fica na tela como lembrete, do
 *  lado do número, para quem está medindo. */
export const ICT_REFERENCIA = 0.5;

export const medidaVazia = (): MedidaDaImagem => ({ cardiaco: null, toracico: null });

export const larguraDe = (c: Compasso | null): number | null =>
  c ? Math.abs(c.x2 - c.x1) : null;

export function calcularIct(m: MedidaDaImagem | null | undefined): number | null {
  const coracao = larguraDe(m?.cardiaco ?? null);
  const torax = larguraDe(m?.toracico ?? null);
  if (coracao === null || torax === null || torax === 0) return null;
  return coracao / torax;
}

/* -------------------------------------------------------------- centímetro --- */

export const temCalibracao = (c: Calibracao | null | undefined): boolean =>
  !!c && !!c.mmPorPixel && !!c.larguraPx;

/** Centímetros de um compasso, quando se conhece o tamanho do pixel.
 *
 *  A fração vira pixel multiplicando pela largura da chapa de laudo, e o pixel
 *  vira milímetro pelo espaçamento do detector. Não importa em que tamanho a
 *  imagem está na tela: a fração não depende disso.
 *
 *  A ressalva clínica é grande e não cabe no número: numa radiografia de tórax
 *  isto é o tamanho NA CHAPA, não o do órgão. Sem a distância foco-filme não há
 *  como corrigir a ampliação, que no coração chega a 10 ou 15%. Por isso o que
 *  decide é o índice, e o centímetro entra como apoio. */
export function centimetros(c: Compasso | null, cal: Calibracao | null | undefined): number | null {
  const fracao = larguraDe(c);
  if (fracao === null || !cal?.mmPorPixel || !cal.larguraPx) return null;
  return (fracao * cal.larguraPx * cal.mmPorPixel) / 10;
}

/** "15,2 cm" -- uma casa, que é o que a medida comporta. */
export const formatarCm = (v: number): string => `${v.toFixed(1).replace('.', ',')} cm`;

/* ----------------------------------------------------------- texto do laudo --- */

/** "0,46" -- duas casas, vírgula, como o médico escreve no laudo. */
export const formatarIct = (v: number): string => v.toFixed(2).replace('.', ',');

/** A linha que entra no texto do laudo. Uma só, no vocabulário do laudo. */
export const textoDoIct = (v: number): string => `Índice cardiotorácico: ${formatarIct(v)}`;

/** Só espaço no começo, e nunca `\s`: em modo multilinha o `\s` engoliria a
 *  quebra anterior, e a substituição levaria a linha de cima junto. */
const LINHA_DO_ICT = /^[ \t]*Índice cardiotorácico:.*$/m;

/** Escreve -- ou atualiza -- a linha do índice num texto de laudo.
 *
 *  Atualiza em vez de acrescentar. O caminho normal é medir, escrever, reparar
 *  que a ponta ficou um pouco fora, arrastar e escrever de novo. Acrescentando,
 *  o laudo sairia assinado com dois índices diferentes, e nem quem escreveu
 *  saberia depois qual dos dois vale. */
export function comIct(texto: string, v: number): string {
  const linha = textoDoIct(v);
  if (LINHA_DO_ICT.test(texto)) return texto.replace(LINHA_DO_ICT, linha);
  const base = texto.replace(/\s+$/, '');
  return base ? `${base}\n${linha}` : linha;
}

/** Tira a linha do índice do texto. Leva junto a quebra que vinha antes dela,
 *  senão desmarcar deixaria uma linha em branco no meio do laudo. */
export function semIct(texto: string): string {
  return texto.replace(/\n?[ \t]*Índice cardiotorácico:[^\n]*/, '').replace(/^\n+/, '');
}

/** O índice que está escrito no texto, ou null. Serve para a tela saber, ao
 *  reabrir o laudo, se aquela medida já foi para o documento. */
export function ictEscrito(texto: string | null | undefined): number | null {
  const achado = /^[ \t]*Índice cardiotorácico:[ \t]*(\d+),(\d+)[ \t]*$/m.exec(texto ?? '');
  if (!achado) return null;
  const v = Number(`${achado[1]}.${achado[2]}`);
  return Number.isFinite(v) ? v : null;
}

/* ------------------------------------------------------------- vindo do banco --- */

function compasso(bruto: unknown): Compasso | null {
  if (!bruto || typeof bruto !== 'object') return null;
  const b = bruto as Record<string, unknown>;
  const fracao = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : null;
  const y = fracao(b.y);
  const x1 = fracao(b.x1);
  const x2 = fracao(b.x2);
  if (y === null || x1 === null || x2 === null) return null;
  return { y, x1, x2 };
}

/** O que vem do banco é jsonb, ou seja: qualquer coisa, inclusive o formato de
 *  uma versão anterior. Entra aqui e sai no formato de hoje, ou sai vazio --
 *  nunca quebra a tela de laudo por causa de uma marca mal gravada. */
export function normalizarMedidas(bruto: unknown): Medidas {
  if (!bruto || typeof bruto !== 'object') return {};
  const saida: Medidas = {};
  for (const [id, valor] of Object.entries(bruto as Record<string, unknown>)) {
    if (!valor || typeof valor !== 'object') continue;
    const v = valor as Record<string, unknown>;
    const m: MedidaDaImagem = { cardiaco: compasso(v.cardiaco), toracico: compasso(v.toracico) };
    // Imagem sem nenhuma marca não precisa de entrada: `medidas` fica nulo no
    // laudo em que ninguém mediu, em vez de guardar um objeto de nulos.
    if (m.cardiaco || m.toracico) saida[id] = m;
  }
  return saida;
}

/** Vazio devolve null para o banco, e não `{}`: "ninguém mediu" e "mediu e
 *  apagou" são a mesma coisa, e nulo é como a coluna nasceu. */
export function medidasParaGravar(m: Medidas): Medidas | null {
  return Object.keys(m).length ? m : null;
}
